import { Alert } from '@/components/alert';
import { Form, TextArea } from '@/components/form';
import { FormattedMessage, useIntl } from '@/components/formatted_message/FormattedMessage';
import { Flex } from '@/components/layouting';
import type { FC } from 'react';
import { MissingCode as messages } from '../messages';
import { LostDeviceStep } from '../enums/lost-device-step';
import { ButtonAnt } from '@/components/buttons/button/ButtonAnt';
import { AuthContentLayout } from '@/routes/auth/components/AuthContentLayout';
import { useAuthContext } from '@/contexts/AuthContext';

const MISSING_CODE_FORM_ID = 'missing-code-form';

interface MissingCodeContentProps {
  onStepChange: (step: LostDeviceStep) => void;
}

interface MissingCodeFormValues {
  message: string;
}

export const MissingCodeContent: FC<MissingCodeContentProps> = ({ onStepChange }) => {
  const intl = useIntl();
  const [form] = Form.useForm<MissingCodeFormValues>();
  const {
    identifiedUser,
    missingRecoveryCodeAsync,
    error: { missingRecoveryCode: missingRecoveryCodeError },
    loading: { isLoadingMissingRecoveryCode },
  } = useAuthContext();

  const handleFinish = async ({ message }: MissingCodeFormValues) => {
    try {
      await missingRecoveryCodeAsync({
        email: identifiedUser.identifier,
        message,
      });

      onStepChange(LostDeviceStep.SuccessAdminNotified);
    } catch (e: unknown) {
      console.error('Error notifying admin about missing recovery code:', e);
    }
  };

  return (
    <AuthContentLayout
      alert={
        <>
          {Boolean(missingRecoveryCodeError) && (
            <Alert showIcon type="error" description={<FormattedMessage {...messages.errorMessage} />} />
          )}
          <Alert showIcon type="info" description={<FormattedMessage {...messages.alertMessage} />} />
        </>
      }
      actions={
        <Flex gap={8} vertical>
          <ButtonAnt
            block
            form={MISSING_CODE_FORM_ID}
            htmlType="submit"
            loading={isLoadingMissingRecoveryCode}
            size="large"
            type="primary"
          >
            <FormattedMessage {...messages.submit} />
          </ButtonAnt>
          <ButtonAnt block onClick={() => onStepChange(LostDeviceStep.RecoveryMethod)} size="large">
            <FormattedMessage {...messages.cancel} />
          </ButtonAnt>
        </Flex>
      }
    >
      <Form
        id={MISSING_CODE_FORM_ID}
        layout="vertical"
        form={form}
        onFinish={handleFinish}
        initialValues={{ message: '' }}
      >
        <Form.Item label={intl.formatMessage(messages.label)} name="message">
          <TextArea placeholder={intl.formatMessage(messages.placeholder)} rows={4} />
        </Form.Item>
      </Form>
    </AuthContentLayout>
  );
};
